import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Plus } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import { useNotice } from "../shared/useNotice";
import {
  getPricingCoverage,
  maintenanceApi,
  PricingCoverageResponse,
  StationCoverage,
  CoverageOrgRow,
  SuggestedOverride,
} from "../../../api/maintenance";

// ── Maintenance aesthetic ────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const th: React.CSSProperties = {
  borderBottom: "1px solid #e0e0e0",
  padding: "8px 10px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#777",
  textTransform: "uppercase",
  letterSpacing: ".03em",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
  padding: "8px 10px",
  color: "#333",
  verticalAlign: "top",
};

const chipBase: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const primaryBtn: React.CSSProperties = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

// Org status chip palette (stable / fragile / broken).
const STATUS_CHIP: Record<CoverageOrgRow["status"], React.CSSProperties> = {
  stable: { background: "#e8f7ef", color: "#1a7f4b" },
  fragile: { background: "#fff7e6", color: "#b9770e" },
  broken: { background: "#fdecec", color: "#c0392b" },
};

const STATUS_HELP: Record<CoverageOrgRow["status"], string> = {
  stable: "An override resolves for this org — pricing survives device-rate removal.",
  fragile:
    "Only a time-bound override exists; it expires to the device rate and will break once the fallback is removed. Pin it as a default.",
  broken:
    "No override resolves for this org — sessions would be rejected once the device rate is removed.",
};

// Wide window used by default overrides (mirrors PricingOverrideForm).
const DEFAULT_VALID_FROM = "2000-01-01T00:00:00";
const DEFAULT_VALID_THRU = "2099-12-31T23:59:59";
const SERVICE_TYPE_KEY = "charging_inclusive_18";

const PricingCoverage: React.FC = () => {
  const [data, setData] = useState<PricingCoverageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");
  const [message, setMessage] = useNotice("success", "");
  const [creatingKey, setCreatingKey] = useState<string | null>(null);

  const canEdit = canEditMaintenance();

  const fetchCoverage = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getPricingCoverage();
      setData(res);
    } catch (err) {
      console.error("Failed to load pricing coverage:", err);
      setError("Failed to load pricing coverage");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoverage();
  }, [fetchCoverage]);

  const handleCreate = async (
    station: StationCoverage,
    suggestion: SuggestedOverride,
    key: string,
  ) => {
    if (suggestion.suggested_price == null) return;
    setCreatingKey(key);
    setError("");
    setMessage("");
    try {
      await maintenanceApi.createPricingOverride({
        org_id: suggestion.org_id,
        charging_station_id: station.station_id,
        charger_type: "AC",
        price_per_kwh: suggestion.suggested_price,
        valid_from: DEFAULT_VALID_FROM,
        valid_thru: DEFAULT_VALID_THRU,
        is_default: true,
        priority: 0,
        service_type_key: SERVICE_TYPE_KEY,
      });
      setMessage(
        `Created override: ${suggestion.org_name} @ ${station.station_name} (₹${suggestion.suggested_price})`,
      );
      await fetchCoverage();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create override");
    } finally {
      setCreatingKey(null);
    }
  };

  const gapStations = data ? data.stations.filter((s) => s.status === "gaps") : [];

  return (
    <MaintenanceLayout title="Settlement · Pricing coverage">
      {loading ? (
        <p style={{ color: "#777" }}>Loading…</p>
      ) : error && !data ? (
        <div
          style={{
            ...card,
            background: "#fdecec",
            border: "1px solid #c0392b",
            color: "#c0392b",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      ) : data ? (
        <>
          {/* Readiness banner */}
          {data.with_gaps === 0 ? (
            <div
              style={{
                ...card,
                background: "#e8f7ef",
                border: "1px solid #1a7f4b",
                color: "#1a7f4b",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <ShieldCheck size={20} />
              <span style={{ fontWeight: 600 }}>
                &#10003; All {data.total_stations} stations covered — safe to rely on
                override-only pricing
              </span>
            </div>
          ) : (
            <div
              style={{
                ...card,
                background: "#fdecec",
                border: "1px solid #c0392b",
                color: "#c0392b",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <AlertTriangle size={20} />
              <span style={{ fontWeight: 600 }}>
                {data.with_gaps} of {data.total_stations} stations have pricing gaps
              </span>
            </div>
          )}

          {/* Inline action feedback */}
          {error && (
            <div
              style={{
                ...card,
                background: "#fdecec",
                border: "1px solid #c0392b",
                color: "#c0392b",
              }}
            >
              {error}
            </div>
          )}
          {message && (
            <div
              style={{
                ...card,
                background: "#e8f7ef",
                border: "1px solid #1a7f4b",
                color: "#1a7f4b",
              }}
            >
              {message}
            </div>
          )}

          {/* Legend */}
          <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 16 }}>
            {(["stable", "fragile", "broken"] as CoverageOrgRow["status"][]).map((s) => (
              <span
                key={s}
                title={STATUS_HELP[s]}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "help" }}
              >
                <span style={{ ...chipBase, ...STATUS_CHIP[s] }}>{s}</span>
                <span style={{ fontSize: 12, color: "#777" }}>{STATUS_HELP[s]}</span>
              </span>
            ))}
          </div>

          {/* Stations with gaps */}
          <div style={card}>
            <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 600, color: "#333" }}>
              Stations with pricing gaps ({gapStations.length})
            </h3>

            {gapStations.length === 0 ? (
              <p style={{ color: "#777", margin: 0 }}>
                No stations with gaps — every station resolves via an override.
              </p>
            ) : (
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={th}>Station</th>
                    <th style={th}>Type</th>
                    <th style={th}>Operator</th>
                    <th style={{ ...th, textAlign: "right" }}>Sessions</th>
                    <th style={th}>Orgs</th>
                    <th style={{ ...th, textAlign: "right" }}>Device &#8377;</th>
                    <th style={th}>Suggestions</th>
                  </tr>
                </thead>
                <tbody>
                  {gapStations.map((station) => (
                    <tr key={station.station_id}>
                      <td style={{ ...td, fontWeight: 600 }}>{station.station_name}</td>
                      <td style={td}>{station.station_type ?? "—"}</td>
                      <td style={td}>{station.operator_org_name}</td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {station.total_sessions.toLocaleString()}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {station.orgs.length === 0 ? (
                            <span style={{ color: "#999", fontSize: 12 }}>—</span>
                          ) : (
                            station.orgs.map((org) => (
                              <span
                                key={org.org_id}
                                title={`${org.sessions.toLocaleString()} sessions · ${STATUS_HELP[org.status]}`}
                                style={{ ...chipBase, ...STATUS_CHIP[org.status], cursor: "help" }}
                              >
                                {org.org_name}: {org.status}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {station.device_rate == null
                          ? "—"
                          : `₹${station.device_rate}`}
                      </td>
                      <td style={td}>
                        {station.suggestions.length === 0 ? (
                          <span style={{ color: "#999", fontSize: 12 }}>—</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {station.suggestions.map((sug, idx) => {
                              const key = `${station.station_id}:${sug.org_id}:${sug.kind}:${idx}`;
                              const priceLabel =
                                sug.suggested_price == null
                                  ? "—"
                                  : sug.suggested_price.toString();
                              const noPrice = sug.suggested_price == null;
                              return (
                                <div
                                  key={key}
                                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                                >
                                  <span
                                    title={sug.reason}
                                    style={{ fontSize: 12.5, color: "#444", cursor: "help" }}
                                  >
                                    {sug.kind} · {sug.org_name} · &#8377;{priceLabel}
                                  </span>
                                  {canEdit && (
                                    <button
                                      style={{
                                        ...primaryBtn,
                                        ...(noPrice || creatingKey === key
                                          ? { background: "#9bb8f0", cursor: "not-allowed" }
                                          : null),
                                      }}
                                      disabled={noPrice || creatingKey === key}
                                      title={
                                        noPrice
                                          ? "No suggested price — set one manually in Pricing Management"
                                          : "Create a default override for this org"
                                      }
                                      onClick={() => handleCreate(station, sug, key)}
                                    >
                                      <Plus size={13} />
                                      {creatingKey === key ? "Creating…" : "Create"}
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </MaintenanceLayout>
  );
};

export default PricingCoverage;
